// UI adapters for the existing /find-ride workflows. Mutation APIs remain unchanged.
export type RidePoint = { latitude: number; longitude: number; label: string };
export type CompactRideRow = {
  id: number; name: string; photo: string | null; from: string; to: string;
  seats: number; dateTime: string; latitude: number | null; longitude: number | null;
};
export type NearbyRide = CompactRideRow & {
  status: string; route: [number, number][]; destination: RidePoint | null;
};
export type ExploreWhen = "now" | "today" | "tomorrow";
export type RideEstimate = { distanceKm: number; durationMinutes: number; coordinates: [number, number][] };
export type PassengerRequestInput = {
  mode: "Now" | "Scheduled"; pickup: RidePoint | null; destination: RidePoint | null;
  route: RideEstimate | null; seats: string; vehicle: string; contribution: string;
  bookingType?: "Seat" | "Parcel";
  parcelImage?: string;
  parcelDescription?: string;
  parcelWeightKg?: string;
  receiverPhone?: string;
  date: string; hour: string; minute: string; period: "AM" | "PM";
};
export function isRidePoint(point: RidePoint | null): point is RidePoint {
  return !!point && point.label.trim() !== "" && Number.isFinite(point.latitude) && Math.abs(point.latitude) <= 90 &&
    Number.isFinite(point.longitude) && Math.abs(point.longitude) <= 180;
}
export function parseRideCoordinates(value: unknown): [number, number][] {
  if (!Array.isArray(value)) return [];
  return value.filter((point): point is [number, number] => Array.isArray(point) && point.length >= 2 &&
    typeof point[0] === "number" && typeof point[1] === "number" && Number.isFinite(point[0]) && Number.isFinite(point[1]) &&
    Math.abs(point[0]) <= 90 && Math.abs(point[1]) <= 180).map(point => [point[0], point[1]]);
}
export function parseRideEstimate(value: unknown): RideEstimate {
  const data = value as { distanceKm?: unknown; durationMinutes?: unknown; route?: { coordinates?: unknown } } | null;
  const coordinates = parseRideCoordinates(data?.route?.coordinates);
  if (typeof data?.distanceKm !== "number" || !Number.isFinite(data.distanceKm) || data.distanceKm <= 0 || data.distanceKm > 5000 ||
      typeof data.durationMinutes !== "number" || !Number.isInteger(data.durationMinutes) || data.durationMinutes < 1 || data.durationMinutes > 10000 ||
      coordinates.length < 2 || coordinates.length > 10000) {
    throw new Error("A valid road route is required before posting a request.");
  }
  return { distanceKm: data.distanceKm, durationMinutes: data.durationMinutes, coordinates };
}

export function buildPassengerRequest(input: PassengerRequestInput, now = new Date()) {
  const { pickup, destination, route } = input;
  if (!isRidePoint(pickup)) throw new Error("Please select your exact starting point.");
  if (!isRidePoint(destination)) throw new Error("Please select your exact destination.");
  if (!route) throw new Error("Please wait for the road route calculation on the map.");
  // Same date/time, seat, contribution and expiry behavior as submitPassengerRequest in /find-ride.
  let rideDate: Date;
  if (input.mode === "Now") rideDate = new Date(now);
  else {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.date)) throw new Error("Please select ride date.");
    if (input.hour) {
      const h = Number(input.hour), m = Number(input.minute);
      if (!Number.isInteger(h) || h < 1 || h > 12 || !Number.isInteger(m) || m < 0 || m > 59) throw new Error("Valid time required.");
      const h24 = input.period === "AM" ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);
      rideDate = new Date(`${input.date}T${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      if (Number.isNaN(rideDate.getTime()) || rideDate.getTime() <= now.getTime()) throw new Error("Scheduled time must be in the future.");
    } else {
      rideDate = new Date(`${input.date}T12:00`);
      const today = new Date(now); today.setHours(0, 0, 0, 0);
      const selected = new Date(`${input.date}T00:00`); selected.setHours(0, 0, 0, 0);
      if (Number.isNaN(selected.getTime()) || selected.getTime() < today.getTime()) throw new Error("Date cannot be in the past.");
    }
  }
  if (input.mode === "Now" && input.bookingType === "Parcel" &&
      !/^[6-9][0-9]{9}$/.test(input.receiverPhone?.trim() ?? "")) {
    throw new Error("Enter a valid 10-digit receiver mobile number.");
  }
  const seats = Number(input.seats);
  if (!Number.isInteger(seats) || seats < 1 || seats > 20) throw new Error("Seats between 1 and 20.");
  const contribution = input.contribution.trim() === "" ? null : Number(input.contribution);
  if (contribution !== null && (!Number.isFinite(contribution) || contribution < 0)) throw new Error("Enter a valid contribution amount.");
  return {
    fromLocation: pickup.label.trim(), fromLatitude: pickup.latitude, fromLongitude: pickup.longitude,
    toLocation: destination.label.trim(), toLatitude: destination.latitude, toLongitude: destination.longitude,
    neededOn: rideDate.toISOString(), timeSpecified: input.mode === "Scheduled" ? Boolean(input.hour) : true,
    requestMode: input.mode, rideNowExpiresAt: input.mode === "Now" ? new Date(now.getTime() + 15 * 60 * 1000).toISOString() : null,
    seats, preferredVehicle: input.vehicle, contribution,
    bookingType: input.mode === "Now" && input.bookingType === "Parcel"
      ? "Parcel" : "Seat",
    parcelImage: input.mode === "Now" && input.bookingType === "Parcel"
      ? input.parcelImage ?? null : null,
    parcelDescription: input.mode === "Now" && input.bookingType === "Parcel"
      ? input.parcelDescription ?? null : null,
    parcelWeightKg: input.mode === "Now" && input.bookingType === "Parcel"
      ? (input.parcelWeightKg ? Number(input.parcelWeightKg) : null) : null,
    receiverPhone: input.mode === "Now" && input.bookingType === "Parcel"
      ? input.receiverPhone?.trim() ?? null : null,
    routeDistanceKm: route.distanceKm, routeDurationMin: route.durationMinutes, routeGeometry: route.coordinates,
  };
}

// Segment-distance and time-window behavior matches the existing Nearby Rides tab.
function toRadians(value: number) { return value * Math.PI / 180; }
function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const dLat = toRadians(lat2 - lat1), dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
export function nearbyRouteDistance(point: RidePoint, coordinates: [number, number][]) {
  if (!coordinates.length) return null;
  if (coordinates.length === 1) return distanceKm(point.latitude, point.longitude, coordinates[0][0], coordinates[0][1]);
  let minDistance = Infinity;
  const kmLat = 111.32, kmLon = 111.32 * Math.cos(toRadians(point.latitude));
  for (let i = 0; i < coordinates.length - 1; i++) {
    const s = coordinates[i], e = coordinates[i + 1];
    const sx = (s[1] - point.longitude) * kmLon, sy = (s[0] - point.latitude) * kmLat;
    const ex = (e[1] - point.longitude) * kmLon, ey = (e[0] - point.latitude) * kmLat;
    const dx = ex - sx, dy = ey - sy, length = dx * dx + dy * dy;
    const fraction = Math.max(0, Math.min(1, length > 0 ? -(sx * dx + sy * dy) / length : 0));
    minDistance = Math.min(minDistance, Math.sqrt((sx + fraction * dx) ** 2 + (sy + fraction * dy) ** 2));
  }
  return minDistance;
}
export function selectNearbyRides(rides: NearbyRide[], pickup: RidePoint | null, when: ExploreWhen, radius: number, now = new Date()) {
  if (!isRidePoint(pickup)) return [];
  return rides.flatMap(ride => {
    const time = Date.parse(ride.dateTime);
    if (!Number.isFinite(time) || time < now.getTime() || ["Completed", "Cancelled", "Full"].includes(ride.status)) return [];
    let start: number, end: number;
    if (when === "now") { start = now.getTime(); end = start + 2 * 60 * 60 * 1000; }
    else {
      const target = new Date(now); if (when === "tomorrow") target.setDate(target.getDate() + 1);
      target.setHours(0, 0, 0, 0); start = target.getTime(); target.setHours(23, 59, 59, 999); end = target.getTime();
    }
    if (time < start || time > end) return [];
    let distance = nearbyRouteDistance(pickup, ride.route);
    if (distance === null && ride.latitude !== null && ride.longitude !== null) distance = distanceKm(pickup.latitude, pickup.longitude, ride.latitude, ride.longitude);
    if (distance === null || !Number.isFinite(distance) || distance > radius) return [];
    return [{ ...ride, nearbyDistanceKm: distance }];
  }).sort((a, b) => a.nearbyDistanceKm - b.nearbyDistanceKm);
}
